import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    body: 'Al adquirir el producto digital "Shopify Express 48H", aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te pedimos que no realices la compra.'
  }, {
    heading: '2. Producto Digital',
    body: 'El producto consiste en una guía digital en formato PDF con contenido educativo sobre la configuración de tiendas en línea. El acceso se entrega de forma inmediata tras la confirmación del pago. Este es un producto de información y los resultados pueden variar según la dedicación y esfuerzo del comprador.'
  }, {
    heading: '3. Uso Permitido',
    body: 'La guía es para uso personal y no comercial. Queda estrictamente prohibido reproducir, distribuir, modificar o revender el contenido total o parcial del producto sin autorización expresa por escrito.'
  }, {
    heading: '4. Política de Reembolso',
    body: 'Debido a la naturaleza digital del producto y su entrega inmediata, no se ofrecen reembolsos una vez completada la descarga. Te recomendamos leer la descripción completa del producto antes de realizar tu compra.'
  }, {
    heading: '5. Limitación de Responsabilidad',
    body: 'El contenido de esta guía es únicamente educativo e informativo. No garantizamos resultados financieros específicos. El éxito depende de múltiples factores individuales, incluyendo pero no limitado a: dedicación, mercado, producto elegido y ejecución.'
  }, {
    heading: '6. Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación en esta página.'
  }]
};
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    body: 'Al realizar tu compra, recopilamos únicamente la información necesaria para procesar la transacción: nombre, dirección de correo electrónico y datos de pago. Los datos de pago son procesados de forma segura por nuestro proveedor de pagos y no almacenamos información de tarjetas de crédito.'
  }, {
    heading: '2. Uso de la Información',
    body: 'Tu información personal se utiliza exclusivamente para: entregar el producto digital adquirido, enviar confirmación de compra y recibo, y comunicarnos contigo en caso de ser necesario respecto a tu compra.'
  }, {
    heading: '3. Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Utilizamos cifrado SSL para todas las transacciones.'
  }, {
    heading: '4. Compartir Información',
    body: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios de confianza que nos ayudan a operar nuestro negocio (procesadores de pago), quienes están obligados a mantener la confidencialidad.'
  }, {
    heading: '5. Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.'
  }, {
    heading: '6. Tus Derechos',
    body: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales disponibles en nuestro sitio.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'terms' ? termsContent : activeModal === 'privacy' ? privacyContent : null;
  return <>
      <footer className="bg-navy-900 text-gray-500 py-8 text-center border-t border-navy-800" data-id="element-29">
        <p data-id="element-30">
          © {new Date().getFullYear()} Shopify Express 48H. Todos los derechos
          reservados.
        </p>
        <div className="mt-4 flex justify-center gap-4 text-sm" data-id="element-31">
          <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors underline-offset-2 hover:underline cursor-pointer" data-id="element-32">
            Términos y Condiciones
          </button>
          <span className="text-gray-700" data-id="element-33">|</span>
          <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors underline-offset-2 hover:underline cursor-pointer" data-id="element-34">
            Política de Privacidad
          </button>
        </div>
      </footer>

      {/* Modal Popup */}
      <AnimatePresence data-id="element-35">
        {activeModal && content && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-36">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-37" />

            {/* Modal Card */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-id="element-38">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-navy-900" data-id="element-39">
                <h2 className="text-2xl font-bold text-white tracking-wide" data-id="element-40">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" aria-label="Cerrar" data-id="element-41">
                  <XIcon className="w-6 h-6" data-id="element-42" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1" data-id="element-43">
                {content.sections.map((section, idx) => <div key={idx} data-id="element-44">
                    <h3 className="text-lg font-bold text-navy-900 mb-2 normal-case font-heading tracking-normal" data-id="element-45">
                      {section.heading}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-46">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center" data-id="element-47">
                <button onClick={() => setActiveModal(null)} className="bg-navy-900 text-white font-bold px-8 py-3 rounded-lg hover:bg-navy-800 transition-colors text-sm uppercase tracking-wider" data-id="element-48">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}